import { isPdfFile } from './pdfHelpers';
import { rasterizePdfToPng } from './pdfRasterize';
import { pickPdfPage } from './pagePicker';
import { t } from './i18n';

type UploadProcessorResult = {
  file: File;
  notice?: { type?: 'info' | 'success' | 'warning'; message: string };
  metadata?: Record<string, unknown>;
  optimized?: boolean;
};

/** Second `process` argument from cores that pass it; older cores call `process(file)`. */
type UploadProcessorContext = { maxUploadBytes?: number };

type VeyopFacade = {
  uploads?: {
    registerProcessor: (processor: {
      id: string;
      accept: string[];
      errorMessage?: string;
      maxInputMb?: number;
      priority?: number;
      matches: (file: File) => boolean;
      process: (file: File, context?: UploadProcessorContext) => Promise<UploadProcessorResult>;
    }) => () => void;
  };
};

let registered = false;

function register(Veyop: VeyopFacade): void {
  if (registered || !Veyop.uploads?.registerProcessor) return;
  registered = true;
  Veyop.uploads.registerProcessor({
    id: 'pdf-upload.first-page',
    accept: ['.pdf', 'application/pdf'],
    errorMessage: t('Could not read PDF'),
    priority: 20,
    // Print PDFs often embed full-size photos; only the rendered image counts against the cap.
    maxInputMb: 50,
    matches: isPdfFile,
    process: async (file, context) => {
      const result = await rasterizePdfToPng(file, {
        maxUploadBytes: Number(context?.maxUploadBytes) || 0,
        pickPage: pickPdfPage,
      });
      return {
        file: result.file,
        // Already rendered for print and fitted under the cap: skip the host's 2500 px downscale.
        optimized: true,
        metadata: {
          pageCount: result.pageCount,
          pageNumber: result.pageNumber,
          trimmed: result.trimmed,
          renderDpi: result.renderDpi,
        },
      };
    },
  });
}

/**
 * Addon bundles are module scripts after the designer bundle, which installs `window.Veyop`
 * synchronously; a host that loaded addons first gets one retry once the document has parsed.
 */
function boot(): void {
  const Veyop = (window as unknown as { Veyop?: VeyopFacade }).Veyop;
  if (Veyop) {
    register(Veyop);
    return;
  }
  document.addEventListener('DOMContentLoaded', () => {
    const late = (window as unknown as { Veyop?: VeyopFacade }).Veyop;
    if (late) register(late);
  }, { once: true });
}

boot();
